import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL
  ? `${process.env.REACT_APP_BACKEND_URL}/api`
  : "/api";

/**
 * Ads Hub · full detail page for a single intake.
 *
 * Editable brief fields on the left, creatives grid on the right.
 * "Generate creatives" queues 6 placeholder slots (Zuops wiring lives in Phase 2).
 * "Copy share link" copies the private /share/:token URL to the clipboard.
 */
export default function AdsClientDetail({ clientId, onSaved, onClose }) {
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState("");
  const [draft, setDraft] = useState({});
  const [dirty, setDirty] = useState({});

  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${API}/ads/clients/${clientId}`);
      setClient(res.data);
      // Only overwrite draft when silent (background poll) if user has no unsaved edits.
      setDraft((prev) => {
        if (silent && prev && Object.keys(prev).length > 0) {
          // Keep unsaved fields; only refresh creatives/brief/videos which are server-driven.
          return {
            ...prev,
            creatives: res.data.creatives,
            videos: res.data.videos,
            creative_brief: res.data.creative_brief,
            campaign_brief: res.data.campaign_brief,
            generation_status: res.data.generation_status,
            status: res.data.status,
            zuops_brand_id: res.data.zuops_brand_id,
            logo_url: res.data.logo_url,
          };
        }
        return res.data;
      });
      if (!silent) setDirty({});
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load client");
    }
    if (!silent) setLoading(false);
  }, [clientId]);

  useEffect(() => { load(); }, [load]);

  // Silent poll while generation is in flight — refreshes creatives inline
  // without blanking the page, so the admin can still see + edit the brief.
  useEffect(() => {
    const anyPending = (client?.creatives || []).some(
      (c) => c.status === "pending" || c.status === "generating"
    );
    const genActive = client?.generation_status === "generating";
    if (!anyPending && !genActive) return;
    const id = setInterval(() => load({ silent: true }), 6000);
    return () => clearInterval(id);
  }, [client?.generation_status, client?.creatives, load]);

  const setField = (key, val) => {
    setDraft((d) => ({ ...d, [key]: val }));
    setDirty((d) => ({ ...d, [key]: val }));
  };

  const save = async () => {
    if (Object.keys(dirty).length === 0) return;
    setSaving(true);
    try {
      const res = await axios.patch(`${API}/ads/clients/${clientId}`, { updates: dirty });
      setClient(res.data.client);
      setDraft(res.data.client);
      setDirty({});
      toast.success("Saved");
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
    setSaving(false);
  };

  const generate = async () => {
    const hasKickoff = !!client?.campaign_brief;
    const existing = (client?.creatives || []).length;
    let msg;
    if (hasKickoff) {
      msg = `Add a NEW round of 6 kickoff-sharpened creatives?${existing ? ` (Existing ${existing} stay — you can delete individually.)` : ""}`;
    } else {
      msg = `Kick a fresh creative pass? The Skye Creative Engine will draft 6 more ad variations + 3 hooks.${existing ? ` Old creatives stay — delete individually if you don't want them.` : ""}`;
    }
    if (!window.confirm(msg)) return;
    setGenerating(true);
    // Optimistically flip generation_status locally so the mini-loader appears
    // immediately without blanking the page.
    setClient((c) => c ? { ...c, generation_status: "generating" } : c);
    try {
      const res = await axios.post(`${API}/ads/clients/${clientId}/generate`);
      toast.success(res.data?.using_kickoff_brief ? "Generating with kickoff brief…" : "Generating from intake…");
      await load({ silent: true });  // silent — do NOT blank the page
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generation failed");
      setClient((c) => c ? { ...c, generation_status: "idle" } : c);
    }
    setGenerating(false);
  };

  const sharpenWithViktor = async () => {
    const existing = (client?.creatives || []).length;
    const msg = `Sharpen Round 2 with Viktor?\n\nViktor rewrites the copy from Mike's voice + the kickoff brief. The Skye Creative Engine then renders fresh visuals with Viktor's locked copy baked in.${existing ? `\n\nExisting ${existing} creatives stay — delete individually if you don't want them.` : ""}`;
    if (!window.confirm(msg)) return;
    setGenerating(true);
    setClient((c) => c ? { ...c, generation_status: "generating" } : c);
    try {
      await axios.post(`${API}/ads/clients/${clientId}/generate-round2`);
      toast.success("Sharpening with Viktor…");
      await load({ silent: true });
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Viktor pass failed");
      setClient((c) => c ? { ...c, generation_status: "idle" } : c);
    }
    setGenerating(false);
  };

  const deleteCreative = async (creative_id) => {
    if (!window.confirm("Delete this creative? It will disappear from the client's share page too.")) return;
    // Optimistic — remove locally so page doesn't scroll to top on reload.
    setClient((c) => c ? { ...c, creatives: (c.creatives || []).filter((cr) => cr.creative_id !== creative_id) } : c);
    setDraft((d) => ({ ...d, creatives: (d.creatives || []).filter((cr) => cr.creative_id !== creative_id) }));
    setSelectedCreative(null);
    try {
      await axios.delete(`${API}/ads/clients/${clientId}/creatives/${creative_id}`);
      toast.success("Deleted");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
      await load({ silent: true });  // Recover on failure
    }
  };

  const toggleCreativeHidden = async (creative_id, hidden) => {
    // Optimistic — patch locally so the page doesn't scroll to top.
    setClient((c) => c ? {
      ...c,
      creatives: (c.creatives || []).map((cr) => cr.creative_id === creative_id ? { ...cr, hidden } : cr),
    } : c);
    setDraft((d) => ({
      ...d,
      creatives: (d.creatives || []).map((cr) => cr.creative_id === creative_id ? { ...cr, hidden } : cr),
    }));
    try {
      await axios.patch(`${API}/ads/clients/${clientId}/creatives/${creative_id}`, { hidden });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
      await load({ silent: true });  // Recover on failure
    }
  };

  const rescoreCreative = async (creative_id) => {
    try {
      await axios.post(`${API}/ads/clients/${clientId}/creatives/${creative_id}/rescore`);
      toast.success("Re-scoring… (uses ~1 credit)");
      setTimeout(() => load({ silent: true }), 15000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rescore failed");
    }
  };

  // Fathom kickoff-call pull — admin can paste a recording ID or transcript
  const [fathomOpen, setFathomOpen] = useState(false);
  const [fathomBusy, setFathomBusy] = useState(false);
  const [selectedCreative, setSelectedCreative] = useState(null);
  const pullFathom = async ({ recording_id, transcript_text }) => {
    setFathomBusy(true);
    try {
      const res = await axios.post(`${API}/ads/clients/${clientId}/pull-fathom-call`, {
        recording_id: recording_id || "",
        transcript_text: transcript_text || "",
      });
      toast.success("Kickoff brief attached");
      setClient(res.data.client);
      setDraft(res.data.client);
      setDirty({});
      setFathomOpen(false);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Pull failed");
    }
    setFathomBusy(false);
  };

  const copyShareLink = () => {
    if (!client?.share_token) return;
    const url = `${window.location.origin}/share/${client.share_token}`;
    navigator.clipboard.writeText(url).then(() => toast.success("Share link copied"));
  };

  const hasChanges = useMemo(() => Object.keys(dirty).length > 0, [dirty]);

  if (loading) return <div style={sh.loading} data-testid="detail-loading">Loading…</div>;
  if (error) return <div style={sh.errorBox}>{error}</div>;
  if (!client) return null;

  return (
    <div style={sh.wrap} data-testid="ads-client-detail">
      {/* Header */}
      <div style={sh.header}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={sh.eyebrow}>Client · {client.status || "new"}</div>
          <h1 style={sh.h1} data-testid="detail-business-name">{client.business_name}</h1>
          <div style={sh.subMeta}>
            {client.first_name} {client.last_name} · {client.business_email} · <a
              href={client.website?.startsWith("http") ? client.website : `https://${client.website}`}
              target="_blank" rel="noreferrer" style={sh.link}>{client.website}</a>
          </div>
        </div>
        <div style={sh.headerActions}>
          <button
            onClick={() => setFathomOpen(true)}
            data-testid="detail-pull-fathom"
            style={sh.pillBtn}
          >
            📞 Pull kickoff call
          </button>
          <a
            href={`/start?admin=${clientId}`}
            target="_blank"
            rel="noreferrer"
            data-testid="detail-continue-intake"
            style={sh.pillBtn}
          >
            ✏️ Continue in form
          </a>
          <button onClick={copyShareLink} data-testid="detail-copy-share" style={sh.pillBtn}>
            📋 Copy share link
          </button>
          <a
            href={`/share/${client.share_token}`}
            target="_blank"
            rel="noreferrer"
            data-testid="detail-open-share"
            style={sh.pillBtn}
          >
            ↗ Open share page
          </a>
          <button onClick={onClose} data-testid="detail-close" style={sh.pillBtnGhost}>
            Close
          </button>
        </div>
      </div>

      {fathomOpen && (
        <FathomPullModal
          clientId={clientId}
          onClose={() => setFathomOpen(false)}
          onSubmit={pullFathom}
          busy={fathomBusy}
        />
      )}

      {client.campaign_brief && (
        <CampaignBriefCard brief={client.campaign_brief} summary={client.call_summary} />
      )}

      {client.creative_brief && (
        <CreativeBriefCard brief={client.creative_brief} />
      )}

      {/* Kickoff Checklist — collapsible, saves to client.kickoff_checklist */}
      <KickoffChecklist
        clientId={clientId}
        value={client.kickoff_checklist || {}}
        onSaved={(next) => setClient((c) => ({ ...c, kickoff_checklist: next }))}
      />

      {/* Main grid */}
      <div style={sh.grid}>
        {/* Left column — editable brief */}
        <div style={sh.col}>
          <Section title="Business" tid="section-business">
            <Field label="Business name" value={draft.business_name} onChange={(v) => setField("business_name", v)} tid="edit-business_name" />
            <Field label="Business address" value={draft.business_address} onChange={(v) => setField("business_address", v)} tid="edit-business_address" />
            <Row>
              <Field label="Phone" value={draft.business_phone} onChange={(v) => setField("business_phone", v)} tid="edit-business_phone" />
              <Field label="Email" value={draft.business_email} onChange={(v) => setField("business_email", v)} tid="edit-business_email" />
            </Row>
            <Field label="Website" value={draft.website} onChange={(v) => setField("website", v)} tid="edit-website" />
            <Row>
              <Field label="Contact name" value={`${draft.first_name || ""} ${draft.last_name || ""}`.trim()} onChange={(v) => {
                const [f, ...r] = v.split(" ");
                setField("first_name", f || "");
                setField("last_name", r.join(" "));
              }} tid="edit-contact_name" />
              <Field label="Project lead" value={draft.project_lead} onChange={(v) => setField("project_lead", v)} tid="edit-project_lead" />
            </Row>
          </Section>

          <Section title="AI setter brief" tid="section-ai">
            <TextArea label="AI greeting" value={draft.ai_greeting} onChange={(v) => setField("ai_greeting", v)} tid="edit-ai_greeting" />
            <TextArea label="Company details" value={draft.company_details} onChange={(v) => setField("company_details", v)} tid="edit-company_details" />
            <TextArea label="Qualifying questions" value={draft.qualifying_questions} onChange={(v) => setField("qualifying_questions", v)} tid="edit-qualifying_questions" />
            <TextArea label="FAQs · Objections" value={draft.faqs_objections} onChange={(v) => setField("faqs_objections", v)} tid="edit-faqs_objections" />
            <Field label="Appointment location" value={draft.appointment_location} onChange={(v) => setField("appointment_location", v)} tid="edit-appointment_location" />
          </Section>

          <Section title="Audience & positioning" tid="section-audience">
            <Field label="Ideal customer" value={draft.ideal_customer} onChange={(v) => setField("ideal_customer", v)} tid="edit-ideal_customer" />
            <Field label="Ad service area" value={draft.ad_service_area} onChange={(v) => setField("ad_service_area", v)} tid="edit-ad_service_area" />
            <TextArea label="USPs" value={draft.usps} onChange={(v) => setField("usps", v)} tid="edit-usps" />
            <TextArea label="Competitors" value={draft.competitors} onChange={(v) => setField("competitors", v)} tid="edit-competitors" />
            <TextArea label="Certifications" value={draft.certifications} onChange={(v) => setField("certifications", v)} tid="edit-certifications" />
            <TextArea label="Customer pain points" value={draft.customer_pain_points} onChange={(v) => setField("customer_pain_points", v)} tid="edit-customer_pain_points" />
          </Section>

          <Section title="Brand & budget" tid="section-brand">
            <Field label="Tagline" value={draft.tagline} onChange={(v) => setField("tagline", v)} tid="edit-tagline" />
            <Field label="Brand colors" value={draft.brand_colors} onChange={(v) => setField("brand_colors", v)} tid="edit-brand_colors" />
            <Field label="Monthly ad budget" value={draft.monthly_budget} onChange={(v) => setField("monthly_budget", v)} tid="edit-monthly_budget" />
            <TextArea label="Sales scripts / notes" value={draft.sales_scripts_notes} onChange={(v) => setField("sales_scripts_notes", v)} tid="edit-sales_scripts_notes" />
            <TextArea label="Anything else" value={draft.anything_else} onChange={(v) => setField("anything_else", v)} tid="edit-anything_else" />
          </Section>

          {hasChanges && (
            <div style={sh.saveBar} data-testid="detail-save-bar">
              <span style={sh.saveMsg}>{Object.keys(dirty).length} unsaved change{Object.keys(dirty).length > 1 ? "s" : ""}</span>
              <button onClick={save} disabled={saving} data-testid="detail-save-btn" style={{ ...sh.primary, opacity: saving ? 0.6 : 1 }}>
                {saving ? "Saving…" : "Save changes"}
              </button>
            </div>
          )}
        </div>

        {/* Right column — creatives */}
        <div style={sh.col}>
          <div style={sh.creativesHead}>
            <div>
              <div style={sh.eyebrow}>
                Creatives · {client.generation_status || "idle"}
                {(client.generation_status === "generating" ||
                  (client.creatives || []).some(c => c.status === "pending" || c.status === "generating")) && (
                  <span style={sh.inlineSpinner} data-testid="inline-generating-spinner" aria-label="generating">
                    <span style={sh.spinnerDot} />
                    <span style={{ ...sh.spinnerDot, animationDelay: "0.15s" }} />
                    <span style={{ ...sh.spinnerDot, animationDelay: "0.3s" }} />
                    <span style={sh.spinnerLabel}>generating…</span>
                  </span>
                )}
              </div>
              <h2 style={sh.h2}>Draft ads</h2>
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
              {!!client.campaign_brief && (
                <button
                  onClick={sharpenWithViktor}
                  disabled={generating}
                  data-testid="detail-sharpen-viktor-btn"
                  title="Viktor rewrites the copy from Mike's voice + the kickoff brief"
                  style={{
                    ...sh.primary,
                    background: "linear-gradient(135deg, #7c3aed 0%, #dc328c 100%)",
                    opacity: generating ? 0.6 : 1,
                  }}
                >
                  Sharpen with Viktor
                </button>
              )}
              <button
                onClick={generate}
                disabled={generating}
                data-testid="detail-generate-btn"
                style={{ ...sh.primary, opacity: generating ? 0.6 : 1 }}
              >
                {generating ? "Queueing…" : (client.creatives?.length ? "Re-generate" : "Generate creatives")}
              </button>
            </div>
          </div>
          <p style={sh.creativesHint}>
            Powered by the <strong style={{ color: "#dc328c" }}>Skye Creative Engine</strong>. Only creatives scored <strong>70+</strong> are shown — anything below the bar is auto-discarded. Delete or hide individually if you want to tune further.
          </p>

          {(() => {
            const all = client.creatives || [];
            // Filter score<70 out of admin too — keep pending/generating/error visible;
            // ready creatives must be score>=70 (or not-yet-scored) to render.
            const visible = all.filter((c) => {
              if (c.status !== "ready") return true;  // pending / generating / error
              const s = c.score;
              if (s === null || s === undefined) return true;  // still scoring
              if (typeof s !== "number") return true;
              return s >= 70;
            });
            const filteredOut = all.length - visible.length;
            return (
              <>
                {filteredOut > 0 && (
                  <div style={sh.filteredCountPill} data-testid="filtered-count-pill">
                    {filteredOut} low-scoring draft{filteredOut > 1 ? "s" : ""} auto-discarded (score &lt; 70)
                  </div>
                )}
                <CreativesGrid
                  creatives={visible}
                  onDelete={deleteCreative}
                  onToggleHidden={toggleCreativeHidden}
                  onRescore={rescoreCreative}
                  onOpen={setSelectedCreative}
                />
              </>
            );
          })()}

          {/* Video attachments — manual for now */}
          <VideoAttachments
            clientId={clientId}
            videos={client.videos || []}
            onChange={() => load({ silent: true })}
          />
        </div>
      </div>

      {selectedCreative && (
        <CreativeDetailModal
          creative={selectedCreative}
          onClose={() => setSelectedCreative(null)}
          onDelete={() => {
            const id = selectedCreative.creative_id;
            setSelectedCreative(null);
            deleteCreative(id);
          }}
          onRescore={() => rescoreCreative(selectedCreative.creative_id)}
          onToggleHidden={() => toggleCreativeHidden(selectedCreative.creative_id, !selectedCreative.hidden)}
        />
      )}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════
// FATHOM PULL MODAL — 4 ways to attach a kickoff call:
//   1. Auto — searches Fathom for "{business_name} kickoff" and pulls the best match
//   2. Search by meeting name
//   3. Paste a Fathom URL
//   4. Paste raw transcript
// ═════════════════════════════════════════════════════════════════════
function FathomPullModal({ clientId, onClose, onSubmit, busy }) {
  const [tab, setTab] = useState("auto");
  const [query, setQuery] = useState("");
  const [url, setUrl] = useState("");
  const [txt, setTxt] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState("");

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [onClose]);

  const doSearch = async (q) => {
    setSearchError("");
    setSearching(true);
    try {
      const res = await axios.get(`${API}/ads/clients/${clientId}/fathom/search`, {
        params: { q: q || "" },
      });
      setSearchResults(res.data?.results || []);
      if (!(res.data?.results || []).length) {
        setSearchError(`No Fathom meetings matched "${res.data?.query || q}"`);
      }
    } catch (e) {
      setSearchError(e?.response?.data?.detail || "Search failed");
    }
    setSearching(false);
  };

  const submit = () => {
    if (tab === "auto") onSubmit({ auto_kickoff: true });
    else if (tab === "search" && query.trim()) onSubmit({ search_query: query.trim() });
    else if (tab === "url" && url.trim()) onSubmit({ fathom_url: url.trim() });
    else if (tab === "text" && txt.trim().length > 20) onSubmit({ transcript_text: txt.trim() });
  };

  const pickResult = (r) => onSubmit({ recording_id: String(r.recording_id) });

  const canSubmit = () => {
    if (busy) return false;
    if (tab === "auto") return true;
    if (tab === "search") return query.trim().length >= 2;
    if (tab === "url") return url.trim().length > 5;
    if (tab === "text") return txt.trim().length > 20;
    return false;
  };

  return (
    <div style={fm.overlay} onClick={onClose} data-testid="fathom-modal">
      <div style={fm.panel} onClick={(e) => e.stopPropagation()}>
        <div style={fm.head}>
          <div>
            <div style={fm.eyebrow}>PULL KICKOFF CALL</div>
            <h2 style={fm.title}>Grab the kickoff-call brief</h2>
          </div>
          <button onClick={onClose} data-testid="fathom-modal-close" style={fm.close}>✕</button>
        </div>
        <div style={fm.body}>
          <div style={fm.tabs}>
            <button type="button" onClick={() => setTab("auto")} data-testid="fathom-tab-auto"
              style={{ ...fm.tab, ...(tab === "auto" ? fm.tabActive : {}) }}>Auto</button>
            <button type="button" onClick={() => setTab("search")} data-testid="fathom-tab-search"
              style={{ ...fm.tab, ...(tab === "search" ? fm.tabActive : {}) }}>Search by name</button>
            <button type="button" onClick={() => setTab("url")} data-testid="fathom-tab-url"
              style={{ ...fm.tab, ...(tab === "url" ? fm.tabActive : {}) }}>Paste URL</button>
            <button type="button" onClick={() => setTab("text")} data-testid="fathom-tab-text"
              style={{ ...fm.tab, ...(tab === "text" ? fm.tabActive : {}) }}>Paste transcript</button>
          </div>

          {tab === "auto" && (
            <>
              <div style={fm.autoCard}>
                <div style={{ fontSize: 28, marginBottom: 4 }}>🎯</div>
                <div style={{ fontSize: 15, fontWeight: 700, color: "#fff", marginBottom: 8 }}>
                  We'll auto-find the kickoff call
                </div>
                <div style={{ fontSize: 13, color: "rgba(255,255,255,0.65)", lineHeight: 1.5 }}>
                  Searches your recent Fathom recordings for meetings titled
                  something like <em>"{`{business_name}`} kickoff"</em> AND meetings that
                  have this client's email as an invitee. Picks the best match.
                </div>
              </div>
              <div style={fm.hint}>
                Tip: name your Fathom calls <code>"[Business Name] Kickoff Call"</code> and
                this button becomes one-click.
              </div>
            </>
          )}

          {tab === "search" && (
            <>
              <label style={fm.label}>Meeting name (or part of it)</label>
              <div style={{ display: "flex", gap: 8 }}>
                <input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && doSearch(query)}
                  data-testid="fathom-search-input"
                  placeholder="Peak Roofing kickoff"
                  style={{ ...fm.input, flex: 1 }}
                  autoFocus
                />
                <button
                  type="button"
                  onClick={() => doSearch(query)}
                  disabled={query.trim().length < 2 || searching}
                  style={{ ...sh.primary, opacity: (query.trim().length < 2 || searching) ? 0.5 : 1 }}
                  data-testid="fathom-search-btn"
                >
                  {searching ? "Searching…" : "Search"}
                </button>
              </div>
              {searchError && <div style={{ ...fm.hint, color: "#FCA5A5" }}>{searchError}</div>}
              {searchResults.length > 0 && (
                <div style={fm.resultsWrap} data-testid="fathom-search-results">
                  {searchResults.map((r) => (
                    <div key={r.recording_id} style={fm.resultRow}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={fm.resultTitle}>{r.meeting_title || `(untitled #${r.recording_id})`}</div>
                        <div style={fm.resultMeta}>
                          {r.recording_start_time?.slice(0, 10) || "—"}
                          {r.meeting_type && ` · ${r.meeting_type}`}
                          {r.invitees?.length ? ` · ${r.invitees.map((i) => i.name || i.email).join(", ")}` : ""}
                        </div>
                      </div>
                      <button
                        onClick={() => pickResult(r)}
                        style={sh.pillBtnGhost}
                        data-testid={`fathom-pick-${r.recording_id}`}
                        disabled={busy}
                      >
                        Pull this
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {tab === "url" && (
            <>
              <label style={fm.label}>Fathom URL</label>
              <input
                type="text"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                data-testid="fathom-url-input"
                placeholder="https://fathom.video/calls/123456789 or https://fathom.video/share/xyz"
                style={fm.input}
                autoFocus
              />
              <div style={fm.hint}>
                Paste any Fathom link — share URL, call URL, or app URL. We'll extract the recording ID.
              </div>
            </>
          )}

          {tab === "text" && (
            <>
              <label style={fm.label}>Transcript text</label>
              <textarea
                value={txt}
                onChange={(e) => setTxt(e.target.value)}
                data-testid="fathom-transcript-text"
                placeholder="Paste the raw transcript (speaker: text lines work great). Great for testing before your first live call lands."
                rows={12}
                style={{ ...fm.input, minHeight: 220, resize: "vertical", lineHeight: 1.5 }}
              />
              <div style={fm.hint}>Minimum 20 characters. Speaker tags optional.</div>
            </>
          )}
        </div>
        <div style={fm.foot}>
          <button onClick={onClose} data-testid="fathom-cancel" style={sh.pillBtnGhost}>Cancel</button>
          {tab !== "search" && (
            <button
              onClick={submit}
              disabled={!canSubmit()}
              data-testid="fathom-submit"
              style={{ ...sh.primary, opacity: !canSubmit() ? 0.6 : 1 }}
            >
              {busy ? "Extracting…" : (tab === "auto" ? "Auto-pull kickoff" : "Attach brief")}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════
// CAMPAIGN BRIEF CARD — surfaces the Claude-extracted brief above the
// intake fields once a kickoff has been attached.
// ═════════════════════════════════════════════════════════════════════
function CampaignBriefCard({ brief, summary }) {
  if (!brief) return null;
  const list = (arr) => Array.isArray(arr) && arr.length > 0;
  return (
    <div style={cb.wrap} data-testid="campaign-brief-card">
      <div style={cb.head}>
        <div>
          <div style={cb.eyebrow}>KICKOFF CALL · CAMPAIGN BRIEF</div>
          <h2 style={cb.h2}>{brief.offer_headline || "Draft offer coming together"}</h2>
        </div>
      </div>
      {summary && <p style={cb.summary}>{summary}</p>}
      <div style={cb.grid}>
        {brief.offer_details && <BriefBlock label="Offer details" text={brief.offer_details} accent="#F472B6" />}
        {brief.service_area && <BriefBlock label="Service area (refined)" text={brief.service_area} accent="#4ADEDE" />}
        {brief.ideal_customer && <BriefBlock label="Ideal customer" text={brief.ideal_customer} accent="#B476FF" />}
        {brief.creative_directions && <BriefBlock label="Creative directions" text={brief.creative_directions} accent="#FBBF24" />}
      </div>
      {list(brief.hook_variations) && (
        <BriefList label="Hook variations" items={brief.hook_variations} accent="#F472B6" tid="brief-hooks" />
      )}
      {list(brief.usps_from_call) && (
        <BriefList label="USPs from call" items={brief.usps_from_call} accent="#4ADE80" tid="brief-usps" />
      )}
      {list(brief.pain_points) && (
        <BriefList label="Pain points" items={brief.pain_points} accent="#60A5FA" tid="brief-pain" />
      )}
      {list(brief.objection_handling) && (
        <BriefList label="Objection handling" items={brief.objection_handling} accent="#F472B6" tid="brief-obj" />
      )}
      {list(brief.avoid_list) && (
        <div style={cb.avoidWrap} data-testid="brief-avoid">
          <div style={{ ...cb.blockLabel, color: "#FF8FB1" }}>⚠ Do NOT claim</div>
          <ul style={cb.list}>
            {brief.avoid_list.map((x, i) => <li key={i} style={{ ...cb.item, color: "#FCA5A5" }}>{x}</li>)}
          </ul>
        </div>
      )}
      {list(brief.next_actions) && (
        <BriefList label="Next actions" items={brief.next_actions} accent="#4ADEDE" tid="brief-next" />
      )}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════
// CREATIVE BRIEF CARD — the Skye Creative Engine brief (post-Claude,
// pre-Zuops). Shows what will be fed into each of the 6 ad slots.
// ═════════════════════════════════════════════════════════════════════
function CreativeBriefCard({ brief }) {
  const [open, setOpen] = useState(true);
  if (!brief) return null;
  const hooks = brief.hook_variations || [];
  const directions = brief.image_directions || [];
  return (
    <div style={crb.wrap} data-testid="creative-brief-card">
      <div style={crb.head} onClick={() => setOpen((v) => !v)}>
        <div>
          <div style={crb.eyebrow}>SKYE CREATIVE ENGINE · CAMPAIGN BRIEF</div>
          <h2 style={crb.h2}>{brief.campaign_name || "Creative brief"}</h2>
          {brief.offer_headline && <p style={crb.offer}>{brief.offer_headline}</p>}
        </div>
        <button style={crb.toggle} data-testid="creative-brief-toggle">
          {open ? "Hide" : "Show"}
        </button>
      </div>
      {open && (
        <>
          {brief.target_audience && (
            <div style={crb.audience}><span style={crb.smallLabel}>AUDIENCE</span> {brief.target_audience}</div>
          )}
          {brief.offer_details && (
            <div style={crb.offerDetails}><span style={crb.smallLabel}>OFFER</span> {brief.offer_details}</div>
          )}
          {hooks.length > 0 && (
            <div style={crb.section}>
              <div style={crb.blockLabel}>Hook variations · {hooks.length}</div>
              <div style={crb.hookList}>
                {hooks.map((h, i) => (
                  <div key={i} style={crb.hookRow} data-testid={`hook-${i}`}>
                    <span style={crb.hookTag}>{(h.formula || "hook").replace(/_/g, " ")}</span>
                    <span style={crb.hookText}>{h.hook}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {directions.length > 0 && (
            <div style={crb.section}>
              <div style={crb.blockLabel}>Image directions · {directions.length}</div>
              <div style={crb.dirGrid}>
                {directions.map((d, i) => (
                  <div key={i} style={crb.dirCard} data-testid={`direction-${i}`}>
                    <div style={crb.dirFormat}>{(d.format || "direction").replace(/_/g, " ")}</div>
                    <div style={crb.dirHead}>{d.headline}</div>
                    {d.subhead && <div style={crb.dirSub}>{d.subhead}</div>}
                    {d.proof_element && <div style={crb.dirProof}>✓ {d.proof_element}</div>}
                    {d.cta && <div style={crb.dirCta}>→ {d.cta}</div>}
                  </div>
                ))}
              </div>
            </div>
          )}
          {Array.isArray(brief.avoid_list) && brief.avoid_list.length > 0 && (
            <div style={crb.avoidWrap} data-testid="creative-avoid">
              <div style={{ ...crb.blockLabel, color: "#FF8FB1" }}>⚠ Do NOT claim</div>
              <ul style={crb.list}>
                {brief.avoid_list.map((x, i) => <li key={i} style={crb.avoidItem}>{x}</li>)}
              </ul>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════
// VIDEO ATTACHMENTS — paste URL (YouTube/Vimeo/Loom/Drive) OR upload MP4.
// Renders on Share Page too.
// ═════════════════════════════════════════════════════════════════════
function VideoAttachments({ clientId, videos, onChange }) {
  const [url, setUrl] = useState("");
  const [headline, setHeadline] = useState("");
  const [attaching, setAttaching] = useState(false);
  const [uploading, setUploading] = useState(false);

  const attachUrl = async (e) => {
    e?.preventDefault?.();
    if (!url.trim()) return;
    setAttaching(true);
    try {
      await axios.post(`${API}/ads/clients/${clientId}/videos`, {
        url: url.trim(),
        headline: headline.trim(),
      });
      setUrl("");
      setHeadline("");
      toast.success("Video attached");
      onChange?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Attach failed");
    }
    setAttaching(false);
  };

  const uploadFile = async (file) => {
    if (!file) return;
    if (file.size > 100 * 1024 * 1024) {
      toast.error("Max 100 MB");
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      if (headline) form.append("headline", headline);
      await axios.post(`${API}/ads/clients/${clientId}/videos/upload`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setHeadline("");
      toast.success("Video uploaded");
      onChange?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    }
    setUploading(false);
  };

  const removeVideo = async (video_id) => {
    if (!window.confirm("Remove this video from the share page?")) return;
    try {
      await axios.delete(`${API}/ads/clients/${clientId}/videos/${video_id}`);
      toast.success("Removed");
      onChange?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Remove failed");
    }
  };

  return (
    <div style={vid.wrap} data-testid="videos-panel">
      <div style={vid.head}>
        <div style={crb.eyebrow}>VIDEO CREATIVES</div>
        <h3 style={vid.h3}>Attach videos to the client's Share Page</h3>
        <p style={vid.hint}>Paste a YouTube / Vimeo / Loom / Drive URL, or upload an MP4 (100 MB max).</p>
      </div>

      <form onSubmit={attachUrl} style={vid.form} data-testid="video-attach-form">
        <input
          type="text"
          placeholder="https://youtube.com/... or https://loom.com/share/..."
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          style={vid.input}
          data-testid="video-url-input"
        />
        <input
          type="text"
          placeholder="Optional headline (e.g. 'Meet the crew')"
          value={headline}
          onChange={(e) => setHeadline(e.target.value)}
          style={vid.input}
          data-testid="video-headline-input"
        />
        <div style={vid.formRow}>
          <button type="submit" disabled={attaching || !url.trim()} style={{ ...vid.attachBtn, opacity: attaching ? 0.5 : 1 }} data-testid="video-attach-btn">
            {attaching ? "Attaching…" : "Attach URL"}
          </button>
          <label style={{ ...vid.uploadBtn, opacity: uploading ? 0.5 : 1 }} data-testid="video-upload-label">
            {uploading ? "Uploading…" : "Upload MP4"}
            <input
              type="file"
              accept="video/mp4,video/quicktime,video/webm"
              onChange={(e) => uploadFile(e.target.files?.[0])}
              disabled={uploading}
              style={{ display: "none" }}
              data-testid="video-upload-input"
            />
          </label>
        </div>
      </form>

      {videos.length === 0 ? (
        <div style={vid.empty}>No videos attached yet.</div>
      ) : (
        <div style={vid.grid} data-testid="videos-grid">
          {videos.map((v) => (
            <VideoRow key={v.video_id} video={v} onRemove={() => removeVideo(v.video_id)} />
          ))}
        </div>
      )}
    </div>
  );
}

function VideoRow({ video, onRemove }) {
  const src = video.embed_url || video.url;
  const isEmbed = ["youtube", "vimeo", "loom", "drive"].includes(video.source);
  return (
    <div style={vid.card} data-testid={`video-row-${video.video_id}`}>
      <div style={vid.player}>
        {isEmbed ? (
          <iframe src={src} title={video.headline || "Video"} allow="autoplay; encrypted-media" allowFullScreen style={vid.iframe} />
        ) : (
          <video src={src} controls preload="metadata" style={vid.videoEl} />
        )}
      </div>
      <div style={vid.cardMeta}>
        <div style={{ flex: 1 }}>
          <div style={vid.sourceTag}>{video.source}</div>
          {video.headline && <div style={vid.cardHead}>{video.headline}</div>}
        </div>
        <button onClick={onRemove} style={vid.remove} data-testid={`video-remove-${video.video_id}`}>
          Remove
        </button>
      </div>
    </div>
  );
}


function BriefBlock({ label, text, accent }) {
  return (
    <div style={cb.block}>
      <div style={{ ...cb.blockLabel, color: accent }}>{label}</div>
      <div style={cb.blockText}>{text}</div>
    </div>
  );
}

function BriefList({ label, items, accent, tid }) {
  return (
    <div style={cb.listWrap} data-testid={tid}>
      <div style={{ ...cb.blockLabel, color: accent }}>{label}</div>
      <ul style={cb.list}>
        {items.map((x, i) => <li key={i} style={cb.item}>{x}</li>)}
      </ul>
    </div>
  );
}

const fm = {
  overlay: {
    position: "fixed", inset: 0, zIndex: 100,
    background: "rgba(0,0,0,0.72)", backdropFilter: "blur(6px)",
    display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
  },
  panel: {
    width: "min(640px, 100%)",
    background: "#12121A",
    border: "1px solid rgba(180,118,255,0.28)",
    borderRadius: 16, overflow: "hidden",
    boxShadow: "0 30px 80px rgba(0,0,0,0.6)",
  },
  head: {
    padding: "18px 22px",
    display: "flex", justifyContent: "space-between", alignItems: "flex-start",
    borderBottom: "1px solid rgba(255,255,255,0.06)",
  },
  eyebrow: {
    fontFamily: "'Poppins', sans-serif",
    fontSize: 10.5, letterSpacing: 1.6, textTransform: "uppercase",
    color: "rgba(180,118,255,0.75)", fontWeight: 700, marginBottom: 6,
  },
  title: { fontSize: 18, fontWeight: 800, color: "#fff", letterSpacing: "-0.015em" },
  close: {
    width: 30, height: 30, borderRadius: 8,
    background: "rgba(255,255,255,0.05)",
    border: "1px solid rgba(255,255,255,0.08)",
    color: "rgba(255,255,255,0.6)", cursor: "pointer",
    fontSize: 14, fontFamily: "'Poppins', sans-serif",
  },
  body: { padding: 22 },
  tabs: { display: "flex", gap: 6, marginBottom: 16 },
  tab: {
    padding: "6px 14px", borderRadius: 999,
    background: "rgba(255,255,255,0.04)",
    border: "1px solid rgba(255,255,255,0.08)",
    color: "rgba(255,255,255,0.7)", cursor: "pointer",
    fontSize: 12, fontWeight: 700, fontFamily: "'Poppins', sans-serif",
  },
  tabActive: {
    background: "rgba(180,118,255,0.14)",
    borderColor: "rgba(180,118,255,0.4)",
    color: "#fff",
  },
  label: { fontSize: 12.5, fontWeight: 700, color: "rgba(255,255,255,0.75)", marginBottom: 6, display: "block" },
  input: {
    width: "100%", padding: "11px 14px",
    background: "rgba(255,255,255,0.03)",
    border: "1px solid rgba(255,255,255,0.10)",
    borderRadius: 10, color: "#fff",
    fontSize: 14, fontFamily: "'Poppins', sans-serif", outline: "none",
  },
  hint: { fontSize: 12, color: "rgba(255,255,255,0.45)", marginTop: 8, lineHeight: 1.5 },
  foot: {
    padding: "14px 22px",
    display: "flex", justifyContent: "flex-end", gap: 8,
    borderTop: "1px solid rgba(255,255,255,0.06)",
    background: "rgba(255,255,255,0.02)",
  },
};

// Creative-detail-modal styles (score card, CPL card, strengths/weaknesses list)
const cd = {
  scoreCard: {
    display: "flex", alignItems: "center", gap: 14,
    background: "rgba(180,118,255,0.06)",
    border: "1px solid rgba(180,118,255,0.24)",
    borderRadius: 12, padding: 14,
  },
  scoreCircle: {
    width: 68, height: 68, borderRadius: "50%",
    background: "rgba(0,0,0,0.4)",
    border: "2px solid rgba(255,255,255,0.14)",
    display: "flex", alignItems: "center", justifyContent: "center",
    flexShrink: 0,
  },
  scoreNum: { fontSize: 26, fontWeight: 800, fontFamily: "'Poppins', sans-serif" },
  scoreLabel: { fontSize: 13.5, color: "#fff", fontWeight: 600, marginBottom: 4 },
  scoreSub: { fontSize: 12, color: "rgba(255,255,255,0.55)", lineHeight: 1.5 },
  cplCard: {
    background: "rgba(255,255,255,0.03)",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: 12, padding: 14,
  },
  cplLabel: {
    fontSize: 10.5, letterSpacing: 1.6, textTransform: "uppercase",
    color: "rgba(255,255,255,0.55)", fontWeight: 700, marginBottom: 6,
  },
  cplRange: { fontSize: 22, fontWeight: 800, color: "#4ADE80", marginBottom: 2 },
  cplHint: { fontSize: 11.5, color: "rgba(255,255,255,0.45)", marginBottom: 10 },
  cplBar: {
    height: 8, borderRadius: 999,
    background: "linear-gradient(90deg, #4ADE80 0%, #FBBF24 50%, #F87171 100%)",
  },
  cplBarFill: { display: "none" },
  cplBarMarker: {
    position: "absolute", top: -6,
    transform: "translateX(-50%)",
    width: 20, height: 20, borderRadius: "50%",
    background: "#fff",
    border: "3px solid #0a0a0a",
    boxShadow: "0 3px 10px rgba(0,0,0,0.35)",
  },
  cplTicks: {
    display: "flex", justifyContent: "space-between",
    fontSize: 11, color: "rgba(255,255,255,0.65)", textAlign: "center",
  },
  cplTicksNew: {
    display: "grid", gridTemplateColumns: "1fr 1fr 1fr",
    alignItems: "center", gap: 8,
  },
  copyPanel: {
    background: "rgba(255,255,255,0.03)",
    border: "1px solid rgba(255,255,255,0.10)",
    borderRadius: 12, padding: 16,
    display: "flex", flexDirection: "column", gap: 10,
  },
  copyEyebrow: {
    fontFamily: "'Poppins', sans-serif",
    fontSize: 10, letterSpacing: 1.6, textTransform: "uppercase",
    color: "rgba(255,255,255,0.55)", fontWeight: 800,
  },
  copyHeadline: { fontSize: 15.5, fontWeight: 800, color: "#fff", lineHeight: 1.25 },
  copySub: { fontSize: 13, color: "rgba(255,255,255,0.72)", lineHeight: 1.55 },
  copyCtaCard: {
    marginTop: 2, padding: "9px 11px",
    background: "rgba(220,50,140,0.08)",
    border: "1px dashed rgba(220,50,140,0.55)",
    borderRadius: 8,
    display: "flex", flexDirection: "column", gap: 2,
  },
  copyCtaLabel: {
    fontFamily: "'Poppins', sans-serif",
    fontSize: 9.5, letterSpacing: 1.4, textTransform: "uppercase",
    color: "#dc328c", fontWeight: 800,
  },
  copyCtaText: { fontSize: 12.5, color: "#fff", fontWeight: 600 },
  listLabel: {
    fontSize: 10.5, letterSpacing: 1.6, textTransform: "uppercase",
    color: "rgba(255,255,255,0.55)", fontWeight: 700, marginBottom: 6,
  },
  list: { listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 6 },
  item: { fontSize: 13, lineHeight: 1.45 },
  emptyScore: {
    background: "rgba(255,255,255,0.03)",
    border: "1px dashed rgba(255,255,255,0.1)",
    borderRadius: 12, padding: 24, textAlign: "center",
  },
};

// Meta Ads Manager copy — dark palette (used in the admin CreativeDetailModal).
const mc = {
  wrap: {
    background: "rgba(24,119,242,0.06)",
    border: "1px solid rgba(24,119,242,0.28)",
    borderRadius: 14, padding: 18,
    fontFamily: "'Poppins', sans-serif",
  },
  head: {
    display: "flex", alignItems: "center", gap: 12,
    marginBottom: 14,
  },
  iconWrap: {
    width: 34, height: 34, borderRadius: "50%",
    background: "#1877F2", color: "#fff", flexShrink: 0,
    display: "flex", alignItems: "center", justifyContent: "center",
    fontSize: 18, fontWeight: 900,
    fontFamily: "Georgia, serif", fontStyle: "italic",
  },
  icon: { lineHeight: 1 },
  eyebrow: {
    fontFamily: "'Poppins', sans-serif",
    fontSize: 10, letterSpacing: 1.6, textTransform: "uppercase",
    color: "#60A5FA", fontWeight: 800, marginBottom: 2,
  },
  title: { fontSize: 12.5, color: "rgba(255,255,255,0.85)", fontWeight: 600 },
  ctaBadge: {
    display: "inline-block", padding: "1px 8px",
    background: "#1877F2", color: "#fff", borderRadius: 4,
    fontSize: 10, fontWeight: 800, letterSpacing: 0.8,
    marginLeft: 4,
  },
  linkPill: {
    padding: "5px 10px", borderRadius: 999,
    background: "rgba(24,119,242,0.15)", border: "1px solid rgba(24,119,242,0.4)",
    color: "#93C5FD", fontSize: 10.5, fontWeight: 700,
    textDecoration: "none", flexShrink: 0,
  },
  variantsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
    gap: 12,
  },
  variantCard: {
    background: "rgba(255,255,255,0.02)",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: 10, padding: 14,
  },
  variantIdx: {
    fontFamily: "'Poppins', sans-serif",
    fontSize: 10, letterSpacing: 1.6, textTransform: "uppercase",
    color: "#60A5FA", fontWeight: 800, marginBottom: 8,
  },
  field: { display: "flex", flexDirection: "column", gap: 5, marginTop: 10 },
  fieldHead: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
  },
  fieldLabel: {
    fontSize: 9.5, letterSpacing: 1.4, textTransform: "uppercase",
    color: "rgba(255,255,255,0.5)", fontWeight: 800,
  },
  copyBtn: {
    fontFamily: "'Poppins', sans-serif",
    fontSize: 10.5, fontWeight: 700, letterSpacing: 0.6,
    padding: "4px 10px", borderRadius: 999,
    cursor: "pointer",
    background: "rgba(24,119,242,0.2)", color: "#93C5FD",
    border: "1px solid rgba(24,119,242,0.5)",
    transition: "all 0.15s ease",
  },
  fieldBody: {
    fontSize: 13, lineHeight: 1.55, borderRadius: 8, padding: "10px 12px",
    fontFamily: "'Poppins', sans-serif",
    whiteSpace: "pre-wrap", wordBreak: "break-word",
    background: "rgba(0,0,0,0.3)", color: "rgba(255,255,255,0.92)",
    border: "1px solid rgba(255,255,255,0.08)",
  },
  fieldBodyMulti: { minHeight: 72 },
};

// Admin Meta stack (dark) — clean best-variant preview on each card.
const ams = {
  wrap: {
    padding: "14px 14px 16px",
    borderTop: "1px solid rgba(24,119,242,0.24)",
    background: "linear-gradient(180deg, rgba(24,119,242,0.08), rgba(122,44,250,0.04))",
    display: "flex", flexDirection: "column", gap: 10,
    fontFamily: "'Poppins', sans-serif",
  },
  brandRow: {
    display: "flex", alignItems: "center", gap: 8,
    paddingBottom: 6,
    borderBottom: "1px solid rgba(255,255,255,0.08)",
  },
  brand: {
    fontSize: 11, letterSpacing: 0.6, textTransform: "uppercase",
    color: "#fff", fontWeight: 800, flex: 1,
  },
  more: {
    fontSize: 9.5, letterSpacing: 0.4, textTransform: "uppercase",
    color: "rgba(255,255,255,0.55)", fontWeight: 700,
    padding: "2px 7px", borderRadius: 999,
    background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)",
  },
  field: { display: "flex", flexDirection: "column", gap: 4 },
  head: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
  },
  label: {
    fontSize: 9.5, letterSpacing: 1.4, textTransform: "uppercase",
    color: "rgba(255,255,255,0.5)", fontWeight: 800,
  },
  copyBtn: {
    display: "inline-flex", alignItems: "center",
    padding: "3px 9px", borderRadius: 999,
    background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.14)",
    color: "rgba(255,255,255,0.85)", fontWeight: 700, fontSize: 10.5,
    fontFamily: "'Poppins', sans-serif",
    cursor: "pointer", letterSpacing: 0.2,
    transition: "background 0.15s ease, color 0.15s ease, border-color 0.15s ease",
  },
  copyBtnActive: {
    background: "#16a34a", borderColor: "#16a34a", color: "#fff",
  },
  value: {
    fontSize: 12.5, lineHeight: 1.55,
    padding: "8px 10px", borderRadius: 8,
    background: "rgba(0,0,0,0.28)",
    border: "1px solid rgba(255,255,255,0.08)",
    color: "rgba(255,255,255,0.92)",
    whiteSpace: "pre-wrap", wordBreak: "break-word",
    maxHeight: 108, overflow: "auto",
  },
  valueMulti: { minHeight: 66 },
};

const cb = {
  wrap: {
    marginBottom: 24,
    background: "linear-gradient(135deg, rgba(74,222,222,0.04), rgba(180,118,255,0.06))",
    border: "1px solid rgba(74,222,222,0.28)",
    borderRadius: 14, padding: 22,
  },
  head: { marginBottom: 8 },
  eyebrow: {
    fontFamily: "'Poppins', sans-serif",
    fontSize: 10.5, letterSpacing: 1.6, textTransform: "uppercase",
    color: "rgba(74,222,222,0.85)", fontWeight: 800, marginBottom: 6,
  },
  h2: {
    fontSize: 22, fontWeight: 800, letterSpacing: "-0.015em",
    color: "#fff", lineHeight: 1.2,
  },
  summary: {
    fontSize: 13.5, color: "rgba(255,255,255,0.75)", lineHeight: 1.6,
    marginTop: 6, paddingBottom: 16,
    borderBottom: "1px solid rgba(255,255,255,0.06)",
  },
  grid: {
    marginTop: 18, display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)", gap: 12,
  },
  block: {
    padding: "10px 14px",
    background: "rgba(255,255,255,0.03)",
    border: "1px solid rgba(255,255,255,0.06)",
    borderRadius: 10,
  },
  blockLabel: {
    fontSize: 10.5, letterSpacing: 1.4, textTransform: "uppercase",
    fontWeight: 800, marginBottom: 6,
    fontFamily: "'Poppins', sans-serif",
  },
  blockText: { fontSize: 13, color: "rgba(255,255,255,0.9)", lineHeight: 1.55 },
  listWrap: {
    marginTop: 14, padding: "10px 14px",
    background: "rgba(255,255,255,0.02)",
    border: "1px solid rgba(255,255,255,0.06)",
    borderRadius: 10,
  },
  list: { margin: 0, paddingLeft: 18, display: "flex", flexDirection: "column", gap: 6 },
  item: { fontSize: 13, color: "rgba(255,255,255,0.85)", lineHeight: 1.5 },
  avoidWrap: {
    marginTop: 14, padding: "10px 14px",
    background: "rgba(255,75,139,0.06)",
    border: "1px solid rgba(255,75,139,0.28)",
    borderRadius: 10,
  },
};

// ═════════════════════════════════════════════════════════════════════
// KICKOFF CHECKLIST — reference questions grouped by theme, per-client
// tracked answers (persist to client.kickoff_checklist).
// ═════════════════════════════════════════════════════════════════════
const CHECKLIST = [
  {
    key: "offer",
    title: "The Offer (highest leverage)",
    accent: "#F472B6",
    questions: [
      { key: "hook", q: "If someone sees your ad, what's the single most compelling offer we can lead with?" },
      { key: "own_offer", q: "Do you already have an offer in mind, or do you want us to come up with one?" },
      { key: "price_guarantee", q: "Any price, guarantee, or urgency we can use? (e.g. $59 tune-up, free inspection, this-month-only, $0 down)" },
      { key: "cant_say", q: "Anything you legally can't say or don't want claimed?" },
    ],
  },
  {
    key: "proof",
    title: "Proof & Credibility",
    accent: "#FBBF24",
    questions: [
      { key: "google", q: "What's your Google rating and roughly how many reviews?" },
      { key: "history", q: "How long in business? Licensed / insured? Certifications or awards?" },
      { key: "family_warranty", q: "Family-owned? Any guarantee or warranty?" },
    ],
  },
  {
    key: "audience",
    title: "Who you want (audience)",
    accent: "#4ADE80",
    questions: [
      { key: "ideal", q: "Who's your ideal customer — homeowner vs renter, age range, income, situation?" },
      { key: "area", q: "Which cities / neighborhoods or radius do you serve?" },
      { key: "top_problems", q: "Top 2-3 problems they come to you for?" },
    ],
  },
  {
    key: "services",
    title: "Services & positioning",
    accent: "#60A5FA",
    questions: [
      { key: "push_hardest", q: "Which services should we push hardest (most profitable / most wanted)?" },
      { key: "differentiator", q: "What makes you different from the other guys in your area?" },
      { key: "emergency_hours", q: "Emergency / same-day? Hours?" },
    ],
  },
];

function KickoffChecklist({ clientId, value, onSaved }) {
  const [expanded, setExpanded] = useState(false);
  const [draft, setDraft] = useState(value);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => { setDraft(value); setDirty(false); }, [value]);

  const totalQs = CHECKLIST.reduce((n, s) => n + s.questions.length, 0);
  const answered = Object.values(draft || {})
    .filter((v) => v && (v.asked || (v.answer && v.answer.trim()))).length;

  const setAnswer = (key, patch) => {
    setDraft((d) => ({ ...d, [key]: { ...(d[key] || {}), ...patch } }));
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/ads/clients/${clientId}`, { updates: { kickoff_checklist: draft } });
      onSaved?.(draft);
      setDirty(false);
      toast.success("Checklist saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
    setSaving(false);
  };

  return (
    <div style={kc.wrap} data-testid="kickoff-checklist">
      <button
        onClick={() => setExpanded(!expanded)}
        data-testid="kickoff-toggle"
        style={kc.header}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span style={kc.headBadge}>KICKOFF CHECKLIST</span>
          <span style={kc.headTitle}>
            {answered} of {totalQs} tracked
          </span>
        </div>
        <span style={kc.chev}>{expanded ? "▲" : "▼"}</span>
      </button>
      {expanded && (
        <div style={kc.body}>
          {CHECKLIST.map((section) => (
            <div key={section.key} style={kc.section}>
              <div style={{ ...kc.sectionTitle, color: section.accent, borderColor: section.accent + "55" }}>
                {section.title}
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                {section.questions.map((q) => {
                  const entry = draft?.[q.key] || {};
                  return (
                    <div key={q.key} style={kc.qBlock}>
                      <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                        <button
                          type="button"
                          onClick={() => setAnswer(q.key, { asked: !entry.asked })}
                          data-testid={`kickoff-check-${q.key}`}
                          style={{
                            ...kc.checkbox,
                            background: entry.asked ? "linear-gradient(135deg, #4ADEDE, #B476FF)" : "transparent",
                            borderColor: entry.asked ? "#B476FF" : "rgba(255,255,255,0.24)",
                            color: entry.asked ? "#0A0A0A" : "transparent",
                          }}
                        >✓</button>
                        <div style={kc.qText}>{q.q}</div>
                      </div>
                      <textarea
                        value={entry.answer || ""}
                        onChange={(e) => setAnswer(q.key, { answer: e.target.value })}
                        data-testid={`kickoff-answer-${q.key}`}
                        placeholder="Their answer…"
                        style={kc.qAnswer}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
          {dirty && (
            <div style={kc.saveBar}>
              <span style={{ fontSize: 12.5, color: "rgba(255,255,255,0.7)" }}>Unsaved changes</span>
              <button
                onClick={save}
                disabled={saving}
                data-testid="kickoff-save-btn"
                style={{ ...sh.primary, opacity: saving ? 0.6 : 1 }}
              >
                {saving ? "Saving…" : "Save checklist"}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const kc = {
  wrap: {
    marginBottom: 20,
    background: "rgba(255,255,255,0.02)",
    border: "1px solid rgba(244,114,182,0.28)",
    borderRadius: 12,
    overflow: "hidden",
  },
  header: {
    width: "100%",
    display: "flex", alignItems: "center", justifyContent: "space-between",
    padding: "14px 18px",
    background: "linear-gradient(90deg, rgba(244,114,182,0.05), transparent)",
    border: "none", cursor: "pointer", color: "#fff",
    fontFamily: "'Poppins', sans-serif",
  },
  headBadge: {
    fontFamily: "'Poppins', sans-serif",
    fontSize: 10, fontWeight: 800, letterSpacing: 1.6,
    color: "#F472B6",
    padding: "3px 8px",
    background: "rgba(244,114,182,0.08)",
    border: "1px solid rgba(244,114,182,0.32)",
    borderRadius: 999,
  },
  headTitle: { fontSize: 13.5, fontWeight: 700, color: "rgba(255,255,255,0.85)" },
  chev: { color: "rgba(255,255,255,0.4)", fontSize: 11, fontFamily: "monospace" },
  body: { padding: "10px 18px 20px", borderTop: "1px solid rgba(255,255,255,0.05)" },
  section: { marginTop: 18 },
  sectionTitle: {
    fontSize: 12.5, fontWeight: 800, letterSpacing: 1, textTransform: "uppercase",
    fontFamily: "'Poppins', sans-serif",
    paddingBottom: 8, marginBottom: 14, borderBottom: "1px solid",
  },
  qBlock: { display: "flex", flexDirection: "column", gap: 8, paddingLeft: 4 },
  qText: {
    fontSize: 13.5, color: "rgba(255,255,255,0.9)", lineHeight: 1.55,
    fontWeight: 500, flex: 1,
  },
  checkbox: {
    width: 22, height: 22, borderRadius: 6,
    border: "1.5px solid",
    display: "inline-flex", alignItems: "center", justifyContent: "center",
    cursor: "pointer", flexShrink: 0,
    fontSize: 12, fontWeight: 900,
    fontFamily: "'Poppins', sans-serif",
    transition: "all 0.18s ease",
    marginTop: 1,
  },
  qAnswer: {
    marginLeft: 32,
    padding: "9px 12px",
    background: "rgba(255,255,255,0.03)",
    border: "1px solid rgba(255,255,255,0.06)",
    borderRadius: 8,
    color: "#fff", fontSize: 13, minHeight: 46,
    resize: "vertical",
    fontFamily: "'Poppins', sans-serif",
    outline: "none",
    lineHeight: 1.5,
  },
  saveBar: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    padding: "12px 14px",
    marginTop: 20,
    background: "rgba(244,114,182,0.06)",
    border: "1px solid rgba(244,114,182,0.28)",
    borderRadius: 10,
  },
};

// ═════════════════════════════════════════════════════════════════════
function Section({ title, tid, children }) {
  return (
    <div style={sh.section} data-testid={tid}>
      <div style={sh.sectionTitle}>{title}</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>{children}</div>
    </div>
  );
}

function Row({ children }) {
  return <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>{children}</div>;
}

function Field({ label, value, onChange, tid }) {
  return (
    <label style={sh.field}>
      <span style={sh.label}>{label}</span>
      <input
        type="text"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        data-testid={tid}
        style={sh.input}
      />
    </label>
  );
}

function TextArea({ label, value, onChange, tid }) {
  return (
    <label style={sh.field}>
      <span style={sh.label}>{label}</span>
      <textarea
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        data-testid={tid}
        style={{ ...sh.input, minHeight: 90, resize: "vertical", lineHeight: 1.5 }}
      />
    </label>
  );
}

function CreativesGrid({ creatives, onDelete, onToggleHidden, onRescore, onOpen }) {
  if (!creatives.length) {
    return (
      <div style={sh.creativesGrid} data-testid="detail-creatives-grid">
        {Array.from({ length: 6 }).map((_, i) => (
          <CreativeSlot key={i} index={i} creative={null} />
        ))}
      </div>
    );
  }
  // Group by round_number (newest first)
  const rounds = {};
  creatives.forEach((c) => {
    const r = c.round_number || 1;
    if (!rounds[r]) rounds[r] = [];
    rounds[r].push(c);
  });
  const roundNums = Object.keys(rounds).map(Number).sort((a, b) => b - a);
  return (
    <div data-testid="detail-creatives-grid">
      {roundNums.map((r) => (
        <div key={r} style={{ marginBottom: 24 }}>
          {roundNums.length > 1 && (
            <div style={sh.roundLabel} data-testid={`round-label-${r}`}>
              Round {String(r).padStart(2, "0")} · {rounds[r].length} creative{rounds[r].length > 1 ? "s" : ""}
            </div>
          )}
          <div style={sh.creativesGrid}>
            {rounds[r].map((c, i) => (
              <CreativeSlot
                key={c.creative_id}
                index={i}
                creative={c}
                onDelete={onDelete}
                onToggleHidden={onToggleHidden}
                onRescore={onRescore}
                onOpen={onOpen}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function CreativeSlot({ index, creative, onDelete, onToggleHidden, onRescore, onOpen }) {
  if (!creative) {
    return (
      <div style={sh.slotEmpty} data-testid={`slot-empty-${index}`}>
        <div style={sh.slotIdx}>Slot {String(index + 1).padStart(2, "0")}</div>
        <div style={sh.slotStatus}>Empty · click Generate</div>
      </div>
    );
  }
  const isReady = creative.status === "ready" && creative.url;
  const isPending = creative.status === "pending" || creative.status === "generating";
  const isError = creative.status === "error";
  const score = creative.score;
  const scoreLabel = creative.score_label || "";
  const scoreColor = score >= 85 ? "#4ADE80" : score >= 70 ? "#FBBF24" : score >= 55 ? "#FB923C" : "#F87171";
  return (
    <div
      style={{
        ...sh.slot,
        ...(creative.hidden ? { opacity: 0.5 } : {}),
      }}
      data-testid={`slot-${creative.creative_id}`}
    >
      <div
        style={{ ...sh.slotImg, cursor: isReady ? "pointer" : "default" }}
        onClick={() => isReady && onOpen?.(creative)}
      >
        {isReady ? (
          <img src={creative.url} alt={creative.headline || `Ad ${index + 1}`} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
        ) : isError ? (
          <div style={sh.pulseWrap}>
            <div style={{ ...sh.pulseLabel, color: "#F87171" }}>Failed</div>
            <div style={{ fontSize: 10, color: "rgba(255,255,255,0.4)", marginTop: 4 }}>{(creative.error || "").slice(0, 60)}</div>
          </div>
        ) : (
          <div style={sh.pulseWrap}>
            <div style={sh.pulse} />
            <div style={sh.pulseLabel}>{isPending ? "Generating…" : "Queued"}</div>
          </div>
        )}
        {creative.format && isReady && (
          <div style={sh.slotFormatBadge}>{creative.format.replace(/_/g, " ")}</div>
        )}
        {typeof score === "number" && isReady && (
          <div style={{ ...sh.slotScoreBadge, background: scoreColor + "cc", borderColor: scoreColor }}>
            <span style={{ fontWeight: 800 }}>{score}</span>
            {scoreLabel && <span style={{ fontSize: 9, opacity: 0.85 }}>{scoreLabel}</span>}
          </div>
        )}
        {creative.hidden && (
          <div style={{ ...sh.belowThresholdChip, background: "rgba(0,0,0,0.75)", left: 10, top: 10 }}>
            🚫 Hidden from share
          </div>
        )}
      </div>
      <div style={sh.slotFooter}>
        <div style={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center", gap: 8 }}>
          <div style={sh.slotIdx}>Ad · {String(index + 1).padStart(2, "0")}</div>
          {creative.client_reaction === "love" && (
            <span
              style={{ ...sh.reactionChip, color: "#dc328c", borderColor: "rgba(220,50,140,0.55)", background: "rgba(220,50,140,0.14)" }}
              data-testid={`admin-reaction-${creative.creative_id}`}
              title="Client loved this creative"
            >❤ Loved by client</span>
          )}
          {creative.client_reaction === "pass" && (
            <span
              style={{ ...sh.reactionChip, color: "rgba(255,255,255,0.72)", borderColor: "rgba(255,255,255,0.22)", background: "rgba(255,255,255,0.06)" }}
              data-testid={`admin-reaction-${creative.creative_id}`}
              title="Client passed on this creative"
            >✕ Client passed</span>
          )}
        </div>
        {isReady ? (
          <div style={{ display: "flex", gap: 6 }}>
            <button
              onClick={(e) => { e.stopPropagation(); onToggleHidden?.(creative.creative_id, !creative.hidden); }}
              style={sh.slotIconBtn}
              title={creative.hidden ? "Show on share page" : "Hide from share page"}
              data-testid={`slot-hide-${creative.creative_id}`}
            >{creative.hidden ? "👁" : "🚫"}</button>
            <button
              onClick={(e) => { e.stopPropagation(); onDelete?.(creative.creative_id); }}
              style={{ ...sh.slotIconBtn, color: "#F87171" }}
              title="Delete forever"
              data-testid={`slot-delete-${creative.creative_id}`}
            >✕</button>
          </div>
        ) : (
          <button
            onClick={(e) => { e.stopPropagation(); onDelete?.(creative.creative_id); }}
            style={{ ...sh.slotIconBtn, color: "#F87171" }}
            title="Delete"
            data-testid={`slot-delete-${creative.creative_id}`}
          >✕</button>
        )}
      </div>
      {/* Meta ads copy — clean vertical stack (dark theme). Shows the best
          variant on the card at a glance; full 5 variants live inside the modal. */}
      {isReady && creative.meta_copy && (
        <AdminMetaStack meta={creative.meta_copy} creativeId={creative.creative_id} />
      )}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════
// META LOGO + ADMIN META STACK (dark best-variant preview on each card)
// + CREATIVE DETAIL MODAL — full 5-variant paste-ready panel
// ═════════════════════════════════════════════════════════════════════
function AdminMetaLogo({ size = 20 }) {
  const gid = `admMetaG-${size}`;
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" style={{ display: "block" }} aria-hidden="true">
      <defs>
        <linearGradient id={gid} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#0064E0" />
          <stop offset="55%" stopColor="#7A2CFA" />
          <stop offset="100%" stopColor="#F02C90" />
        </linearGradient>
      </defs>
      <path
        d="M6 21c0-6.5 3.6-11 8.4-11 3 0 5.3 1.9 7.2 5.4l1.3 2.4c1.5 2.7 2.6 3.9 4.3 3.9 1.6 0 2.7-1.4 2.7-3.9 0-3.7-1.7-6.6-4.3-6.6-1.5 0-2.7 0.7-4.2 2.4l-1.6-2.6C21.5 8.6 23.6 7 26.4 7 30.6 7 33 11 33 15.9c0 4.8-2.3 8.1-6 8.1-2.7 0-4.5-1.5-6.5-4.9L19 15.7c-1.3-2.4-2.4-3.5-3.9-3.5-2.6 0-4.6 2.9-4.6 8.6 0 2.5 1 4.2 2.7 4.2 1.4 0 2.3-0.6 3.4-2l1.6 2.5C16.5 27 14.4 28 12 28 8 28 6 25.6 6 21z"
        transform="translate(-3 -1) scale(0.98)"
        fill={`url(#${gid})`}
      />
    </svg>
  );
}

function AdminMetaStack({ meta, creativeId }) {
  const [copiedKey, setCopiedKey] = useState(null);
  const doCopy = async (e, key, text) => {
    e.stopPropagation();
    if (!text) return;
    try { await navigator.clipboard.writeText(text); } catch { /* no-op */ }
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 1400);
  };
  const variants = Array.isArray(meta.variations) && meta.variations.length
    ? meta.variations
    : [{ primary_text: meta.primary_text, headline: meta.headline, description: meta.description }];
  const best = variants[0] || {};
  const extras = Math.max(0, variants.length - 1);
  const renderField = (label, value, key) => (
    value ? (
      <div style={ams.field} data-testid={`admin-stack-${key}-${creativeId}`}>
        <div style={ams.head}>
          <span style={ams.label}>{label}</span>
          <button
            type="button"
            onClick={(e) => doCopy(e, key, value)}
            style={{ ...ams.copyBtn, ...(copiedKey === key ? ams.copyBtnActive : {}) }}
            data-testid={`admin-stack-${key}-${creativeId}-copy`}
          >
            <span aria-hidden="true" style={{ marginRight: 4 }}>{copiedKey === key ? "✓" : "⧉"}</span>
            {copiedKey === key ? "Copied" : "Copy"}
          </button>
        </div>
        <div style={{ ...ams.value, ...(key === "primary" ? ams.valueMulti : {}) }}>{value}</div>
      </div>
    ) : null
  );
  return (
    <div style={ams.wrap} data-testid={`admin-meta-stack-${creativeId}`} onClick={(e) => e.stopPropagation()}>
      <div style={ams.brandRow}>
        <AdminMetaLogo size={18} />
        <span style={ams.brand}>Meta ads copy</span>
        {extras > 0 && <span style={ams.more}>+{extras} more inside</span>}
      </div>
      {renderField("Headline", best.headline, "headline")}
      {renderField("Primary text", best.primary_text, "primary")}
      {renderField("Description", best.description, "description")}
    </div>
  );
}

function AdminMetaCopyBlock({ meta }) {
  const [copiedKey, setCopiedKey] = useState(null);
  const doCopy = async (key, text) => {
    if (!text) return;
    try { await navigator.clipboard.writeText(text); } catch { /* no-op */ }
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 1400);
  };
  let variants = Array.isArray(meta.variations) ? meta.variations.filter(v => v && (v.primary_text || v.headline)) : [];
  if (!variants.length && (meta.primary_text || meta.headline || meta.description)) {
    variants = [{ primary_text: meta.primary_text, headline: meta.headline, description: meta.description }];
  }
  if (!variants.length) return null;
  const cta = meta.cta_type ? String(meta.cta_type).replace(/_/g, " ") : "LEARN MORE";
  let hostname = "";
  if (meta.link_url) {
    try { hostname = new URL(meta.link_url.startsWith("http") ? meta.link_url : `https://${meta.link_url}`).host; } catch { hostname = meta.link_url; }
  }
  return (
    <div style={mc.wrap} data-testid="admin-meta-copy">
      <div style={mc.head}>
        <div style={mc.iconWrap}><AdminMetaLogo size={22} /></div>
        <div style={{ flex: 1 }}>
          <div style={mc.eyebrow}>Meta ads copy · all variants</div>
          <div style={mc.title}>
            Paste-ready · {variants.length} variant{variants.length > 1 ? "s" : ""} · CTA{" "}
            <span style={mc.ctaBadge}>{cta}</span>
          </div>
        </div>
        {hostname && (
          <a href={meta.link_url} target="_blank" rel="noopener noreferrer" style={mc.linkPill} data-testid="admin-meta-link">
            {hostname} →
          </a>
        )}
      </div>
      <div style={mc.variantsGrid}>
        {variants.map((v, i) => (
          <div key={i} style={mc.variantCard} data-testid={`admin-meta-variant-${i}`}>
            <div style={mc.variantIdx}>Variant {String(i + 1).padStart(2, "0")}</div>
            {v.primary_text && (
              <MetaFieldAdmin label="Primary text" value={v.primary_text} multi
                copied={copiedKey === `p${i}`} onCopy={() => doCopy(`p${i}`, v.primary_text)} tid={`p${i}`} />
            )}
            {v.headline && (
              <MetaFieldAdmin label="Headline" value={v.headline}
                copied={copiedKey === `h${i}`} onCopy={() => doCopy(`h${i}`, v.headline)} tid={`h${i}`} />
            )}
            {v.description && (
              <MetaFieldAdmin label="Description" value={v.description}
                copied={copiedKey === `d${i}`} onCopy={() => doCopy(`d${i}`, v.description)} tid={`d${i}`} />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function MetaFieldAdmin({ label, value, copied, onCopy, tid, multi }) {
  return (
    <div style={mc.field}>
      <div style={mc.fieldHead}>
        <span style={mc.fieldLabel}>{label}</span>
        <button onClick={onCopy} style={mc.copyBtn} data-testid={`admin-meta-copy-${tid}`}>
          {copied ? "✓ Copied" : "Copy"}
        </button>
      </div>
      <div style={{ ...mc.fieldBody, ...(multi ? mc.fieldBodyMulti : {}) }}>{value}</div>
    </div>
  );
}

function CreativeDetailModal({ creative, onClose, onDelete, onRescore, onToggleHidden }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [onClose]);

  const score = typeof creative.score === "number" ? creative.score : null;
  const scoreLabel = creative.score_label || "";
  const cpl = creative.predicted_cpl || {};
  const scoreColor = score >= 85 ? "#4ADE80" : score >= 70 ? "#FBBF24" : score >= 55 ? "#FB923C" : "#F87171";
  const hasScore = score !== null;

  return (
    <div style={fm.overlay} onClick={onClose} data-testid="creative-detail-modal">
      <div style={{ ...fm.panel, maxWidth: 900 }} onClick={(e) => e.stopPropagation()}>
        <div style={fm.head}>
          <div>
            <div style={fm.eyebrow}>AD DETAILS</div>
            <h2 style={fm.title}>{creative.headline || "Untitled creative"}</h2>
            {creative.format && <div style={{ marginTop: 4, fontSize: 12, color: "rgba(255,255,255,0.55)", textTransform: "capitalize" }}>{creative.format.replace(/_/g, " ")}</div>}
          </div>
          <button onClick={onClose} data-testid="creative-detail-close" style={fm.close}>✕</button>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, padding: "0 24px 20px" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <div style={{ background: "#000", aspectRatio: "1 / 1", borderRadius: 12, overflow: "hidden" }}>
              {creative.url ? (
                <img src={creative.url} alt={creative.headline || "ad"} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              ) : null}
            </div>
            {(creative.headline || creative.subhead || creative.cta) && (
              <div style={cd.copyPanel} data-testid="admin-modal-copy">
                <div style={cd.copyEyebrow}>The copy</div>
                {creative.headline && <div style={cd.copyHeadline}>{creative.headline}</div>}
                {creative.subhead && <div style={cd.copySub}>{creative.subhead}</div>}
                {creative.cta && (
                  <div style={cd.copyCtaCard}>
                    <div style={cd.copyCtaLabel}>Call to action</div>
                    <div style={cd.copyCtaText}>{creative.cta}</div>
                  </div>
                )}
              </div>
            )}
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {hasScore ? (
              <>
                <div style={cd.scoreCard}>
                  <div style={cd.scoreCircle}>
                    <div style={{ ...cd.scoreNum, color: scoreColor }}>{score}</div>
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={cd.scoreLabel}>
                      Ad score <span style={{ color: scoreColor, fontWeight: 700 }}>{scoreLabel || (score >= 70 ? "Strong" : "Below threshold")}</span>
                    </div>
                    {(creative.percentile_context || creative.industry_context || score !== null) && (
                      <div style={cd.scoreSub}>
                        {creative.percentile_context || `Above the ${Math.max(50, Math.min(99, score - 6))}th percentile`}
                        {creative.industry_context ? ` for ${creative.industry_context} creatives in the last 90 days.` : " for the last 90 days."}
                      </div>
                    )}
                  </div>
                </div>

                {(cpl.likely || cpl.likely_low || cpl.best_case) && (() => {
                  const bestCase = cpl.best_case ?? cpl.likely_low;
                  const riskCase = cpl.risk_case ?? cpl.likely_high;
                  const likelyMid = cpl.likely ?? (cpl.likely_low && cpl.likely_high ? Math.round((cpl.likely_low + cpl.likely_high) / 2) : null);
                  const markerPct = (bestCase != null && riskCase != null && likelyMid != null && riskCase > bestCase)
                    ? Math.max(4, Math.min(96, Math.round(((likelyMid - bestCase) / (riskCase - bestCase)) * 100)))
                    : 50;
                  const rangeText = cpl.likely_low && cpl.likely_high
                    ? `$${cpl.likely_low}–$${cpl.likely_high}`
                    : (cpl.likely ? `$${cpl.likely}` : "—");
                  return (
                    <div style={cd.cplCard}>
                      <div style={cd.cplLabel}>$ Predicted CPL</div>
                      <div style={cd.cplRange}>{rangeText}</div>
                      <div style={cd.cplHint}>likely cost per lead</div>
                      <div style={{ position: "relative", marginTop: 6, marginBottom: 14 }}>
                        <div style={cd.cplBar} />
                        <div style={{ ...cd.cplBarMarker, left: `${markerPct}%` }} />
                      </div>
                      <div style={cd.cplTicksNew}>
                        <div style={{ textAlign: "left" }}>
                          <div style={{ fontSize: 14, fontWeight: 800, color: "#4ADE80" }}>${bestCase ?? "—"}</div>
                          <div style={{ fontSize: 11, color: "rgba(255,255,255,0.5)" }}>Best case</div>
                        </div>
                        <div style={{ textAlign: "center" }}>
                          <div style={{ fontSize: 14, fontWeight: 800, color: "#fff" }}>{rangeText}</div>
                          <div style={{ fontSize: 11, color: "rgba(255,255,255,0.5)" }}>Likely range</div>
                        </div>
                        <div style={{ textAlign: "right" }}>
                          <div style={{ fontSize: 14, fontWeight: 800, color: "#F87171" }}>${riskCase ?? "—"}</div>
                          <div style={{ fontSize: 11, color: "rgba(255,255,255,0.5)" }}>Risk case</div>
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {Array.isArray(creative.strengths) && creative.strengths.length > 0 && (
                  <div>
                    <div style={cd.listLabel}>Strengths</div>
                    <ul style={cd.list}>{creative.strengths.map((s, i) => <li key={i} style={{ ...cd.item, color: "#4ADE80" }}>✓ {s}</li>)}</ul>
                  </div>
                )}
                {Array.isArray(creative.weaknesses) && creative.weaknesses.length > 0 && (
                  <div>
                    <div style={cd.listLabel}>Weaknesses</div>
                    <ul style={cd.list}>{creative.weaknesses.map((s, i) => <li key={i} style={{ ...cd.item, color: "#FCA5A5" }}>⚠ {s}</li>)}</ul>
                  </div>
                )}
                {Array.isArray(creative.recommendations) && creative.recommendations.length > 0 && (
                  <div>
                    <div style={cd.listLabel}>Recommendations</div>
                    <ul style={cd.list}>{creative.recommendations.map((s, i) => <li key={i} style={{ ...cd.item, color: "#FBBF24" }}>→ {s}</li>)}</ul>
                  </div>
                )}
              </>
            ) : (
              <div style={cd.emptyScore}>
                <div style={{ fontSize: 36, marginBottom: 8 }}>📊</div>
                <div style={{ fontSize: 15, fontWeight: 700, color: "#fff", marginBottom: 6 }}>Not scored yet</div>
                <div style={{ fontSize: 12.5, color: "rgba(255,255,255,0.55)", lineHeight: 1.5 }}>
                  Scores populate automatically after generation. If you need it now, hit "Rescore" (uses ~1 credit).
                </div>
                <button onClick={onRescore} style={{ ...sh.primary, marginTop: 16 }} data-testid="detail-rescore-btn">Score now</button>
              </div>
            )}
          </div>
        </div>

        {/* Facebook Ads Manager copy — full width under both columns */}
        {creative.meta_copy && (
          <div style={{ padding: "0 24px 20px" }}>
            <AdminMetaCopyBlock meta={creative.meta_copy} />
          </div>
        )}

        <div style={fm.foot}>
          {hasScore && <button onClick={onRescore} style={sh.pillBtnGhost} data-testid="detail-rescore-refresh">Re-score</button>}
          <button onClick={onToggleHidden} style={sh.pillBtnGhost} data-testid="detail-toggle-hidden">
            {creative.hidden ? "Show on share page" : "Hide from share page"}
          </button>
          <button onClick={onDelete} style={{ ...sh.primary, background: "linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)" }} data-testid="detail-delete-btn">Delete forever</button>
        </div>
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════
const sh = {
  wrap: {
    padding: "24px 32px 60px",
    fontFamily: "'Poppins', system-ui, sans-serif",
    color: "#fff",
  },
  loading: { padding: 60, color: "rgba(255,255,255,0.45)", fontSize: 14 },
  errorBox: { padding: 32, color: "#f87171" },
  header: { display: "flex", alignItems: "flex-start", gap: 16, marginBottom: 28 },
  eyebrow: {
    fontFamily: "'Poppins', sans-serif", fontSize: 10.5, letterSpacing: 1.6,
    textTransform: "uppercase", color: "rgba(244,114,182,0.65)", fontWeight: 700,
    marginBottom: 8,
  },
  h1: {
    fontSize: 30, fontWeight: 800, letterSpacing: "-0.02em",
    marginBottom: 6, color: "#fff",
  },
  h2: {
    fontSize: 20, fontWeight: 800, letterSpacing: "-0.015em", color: "#fff", marginTop: 4,
  },
  subMeta: {
    fontSize: 12.5, color: "rgba(255,255,255,0.5)", lineHeight: 1.6,
    overflow: "hidden", textOverflow: "ellipsis",
  },
  link: { color: "#F472B6", textDecoration: "none", fontWeight: 700 },
  headerActions: { display: "flex", gap: 8, flexShrink: 0 },
  pillBtn: {
    padding: "8px 14px", borderRadius: 999,
    background: "rgba(244,114,182,0.08)",
    border: "1px solid rgba(244,114,182,0.28)",
    color: "#F472B6", cursor: "pointer",
    fontSize: 12, fontWeight: 700,
    fontFamily: "'Poppins', sans-serif", textDecoration: "none",
    display: "inline-flex", alignItems: "center", gap: 6,
  },
  pillBtnGhost: {
    padding: "8px 14px", borderRadius: 999,
    background: "rgba(255,255,255,0.04)",
    border: "1px solid rgba(255,255,255,0.08)",
    color: "rgba(255,255,255,0.6)", cursor: "pointer",
    fontSize: 12, fontWeight: 700,
    fontFamily: "'Poppins', sans-serif",
  },
  grid: { display: "grid", gridTemplateColumns: "minmax(0, 5fr) minmax(0, 4fr)", gap: 32, alignItems: "start" },
  col: { minWidth: 0 },
  section: {
    background: "rgba(255,255,255,0.02)",
    border: "1px solid rgba(255,255,255,0.06)",
    borderRadius: 12, padding: 20, marginBottom: 20,
  },
  sectionTitle: {
    fontSize: 12, letterSpacing: 1.4, textTransform: "uppercase",
    color: "rgba(255,255,255,0.75)", fontWeight: 800,
    fontFamily: "'Poppins', sans-serif",
    marginBottom: 16, paddingBottom: 12,
    borderBottom: "1px solid rgba(255,255,255,0.06)",
  },
  field: { display: "flex", flexDirection: "column", gap: 6 },
  label: { fontSize: 11.5, fontWeight: 700, color: "rgba(255,255,255,0.6)", letterSpacing: 0.3 },
  input: {
    padding: "9px 12px",
    background: "rgba(255,255,255,0.03)",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: 8, color: "#fff",
    fontSize: 13, fontFamily: "'Poppins', sans-serif",
    outline: "none",
  },
  saveBar: {
    position: "sticky", bottom: 20,
    display: "flex", alignItems: "center", justifyContent: "space-between",
    padding: "12px 16px",
    background: "rgba(15,15,23,0.95)",
    border: "1px solid rgba(244,114,182,0.35)",
    borderRadius: 12, backdropFilter: "blur(10px)",
    boxShadow: "0 8px 24px rgba(0,0,0,0.4)",
    marginTop: 20,
  },
  saveMsg: { fontSize: 12.5, color: "rgba(255,255,255,0.75)", fontWeight: 600 },
  primary: {
    padding: "10px 18px", borderRadius: 999,
    background: "linear-gradient(135deg, #dc328c, #F472B6)",
    color: "#fff", border: "none", cursor: "pointer",
    fontSize: 12.5, fontWeight: 800, letterSpacing: 0.2,
    fontFamily: "'Poppins', sans-serif",
  },
  creativesHead: { display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 12, marginBottom: 10 },
  creativesHint: {
    fontSize: 12, color: "rgba(255,255,255,0.4)",
    lineHeight: 1.6, marginBottom: 20,
    padding: "10px 12px",
    background: "rgba(255,255,255,0.03)",
    border: "1px solid rgba(255,255,255,0.06)",
    borderRadius: 8,
  },
  creativesGrid: {
    display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 12,
  },
  slot: {
    background: "rgba(255,255,255,0.02)",
    border: "1px solid rgba(255,255,255,0.06)",
    borderRadius: 10, overflow: "hidden",
  },
  slotEmpty: {
    aspectRatio: "1 / 1",
    background: "repeating-linear-gradient(45deg, rgba(255,255,255,0.015) 0 8px, transparent 8px 16px)",
    border: "1px dashed rgba(255,255,255,0.12)",
    borderRadius: 10,
    display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 6,
    padding: 20,
  },
  slotImg: {
    aspectRatio: "1 / 1",
    background: "linear-gradient(135deg, #1a0f1e 0%, #0f0f17 100%)",
    display: "flex", alignItems: "center", justifyContent: "center",
    position: "relative", overflow: "hidden",
  },
  slotFooter: {
    padding: "8px 10px",
    display: "flex", justifyContent: "space-between", alignItems: "center",
    borderTop: "1px solid rgba(255,255,255,0.06)",
  },
  slotIdx: {
    fontFamily: "'Poppins', sans-serif", fontSize: 10, letterSpacing: 1.4,
    color: "rgba(255,255,255,0.45)", fontWeight: 700, textTransform: "uppercase",
  },
  slotStatus: {
    fontSize: 10.5, color: "rgba(255,255,255,0.55)", fontWeight: 700, letterSpacing: 0.3,
  },
  pulseWrap: { display: "flex", flexDirection: "column", alignItems: "center", gap: 10 },
  pulse: {
    width: 32, height: 32, borderRadius: "50%",
    background: "linear-gradient(135deg, #dc328c, #F472B6)",
    animation: "adshub-pulse 1.8s ease-in-out infinite",
  },
  pulseLabel: {
    fontFamily: "'Poppins', sans-serif", fontSize: 10,
    letterSpacing: 1.4, textTransform: "uppercase",
    color: "rgba(255,255,255,0.5)", fontWeight: 700,
  },
  slotFormatBadge: {
    position: "absolute", top: 8, left: 8,
    padding: "3px 8px", borderRadius: 999,
    background: "rgba(0,0,0,0.68)",
    border: "1px solid rgba(255,255,255,0.14)",
    color: "rgba(255,255,255,0.9)",
    fontSize: 9.5, fontWeight: 700, letterSpacing: 0.8, textTransform: "uppercase",
    fontFamily: "'Poppins', sans-serif", pointerEvents: "none",
  },
  slotScoreBadge: {
    position: "absolute", top: 8, right: 8,
    padding: "4px 9px", borderRadius: 999,
    border: "1px solid",
    display: "flex", alignItems: "center", gap: 5,
    color: "#0B0B10",
    fontSize: 11, fontFamily: "'Poppins', sans-serif",
    boxShadow: "0 2px 10px rgba(0,0,0,0.25)",
    pointerEvents: "none",
  },
  belowThresholdChip: {
    position: "absolute", bottom: 8, left: 8,
    padding: "3px 8px", borderRadius: 999,
    background: "rgba(220,38,38,0.85)",
    color: "#fff",
    fontSize: 9.5, fontWeight: 700, letterSpacing: 0.6,
    fontFamily: "'Poppins', sans-serif",
    pointerEvents: "none",
  },
  slotHead: {
    fontSize: 12.5, fontWeight: 700, color: "rgba(255,255,255,0.9)",
    marginTop: 2, overflow: "hidden", textOverflow: "ellipsis",
    whiteSpace: "nowrap", maxWidth: 220,
  },
  slotIconBtn: {
    width: 26, height: 26, borderRadius: 6,
    background: "rgba(255,255,255,0.05)",
    border: "1px solid rgba(255,255,255,0.10)",
    color: "rgba(255,255,255,0.7)", cursor: "pointer",
    fontSize: 12, lineHeight: 1,
    display: "flex", alignItems: "center", justifyContent: "center",
    fontFamily: "'Poppins', sans-serif",
  },
  roundLabel: {
    fontFamily: "'Poppins', sans-serif", fontSize: 10.5,
    letterSpacing: 1.6, textTransform: "uppercase",
    color: "rgba(180,118,255,0.75)", fontWeight: 700,
    marginBottom: 10, paddingLeft: 2,
  },
  filteredCountPill: {
    display: "inline-flex", alignItems: "center", gap: 6,
    padding: "6px 12px",
    marginBottom: 14,
    background: "rgba(255,255,255,0.03)",
    border: "1px dashed rgba(255,255,255,0.14)",
    borderRadius: 999,
    color: "rgba(255,255,255,0.55)",
    fontSize: 11.5,
    fontFamily: "'Poppins', sans-serif",
    letterSpacing: 0.2,
  },
  inlineSpinner: {
    display: "inline-flex", alignItems: "center", gap: 4,
    marginLeft: 10,
    padding: "3px 10px 3px 8px",
    background: "rgba(220,50,140,0.14)",
    border: "1px solid rgba(220,50,140,0.4)",
    borderRadius: 999,
    fontFamily: "'Poppins', sans-serif",
    fontSize: 10, fontWeight: 700, letterSpacing: 1.2,
    textTransform: "uppercase",
    color: "#dc328c",
    verticalAlign: "middle",
  },
  spinnerDot: {
    width: 5, height: 5, borderRadius: "50%",
    background: "#dc328c",
    animation: "skye-bounce 0.9s ease-in-out infinite",
    display: "inline-block",
  },
  spinnerLabel: { marginLeft: 4 },
  slotCopy: {
    padding: "10px 12px 12px",
    borderTop: "1px dashed rgba(255,255,255,0.06)",
    display: "flex", flexDirection: "column", gap: 6,
  },
  slotSub: {
    fontSize: 11.5, color: "rgba(255,255,255,0.68)", lineHeight: 1.45,
    fontStyle: "italic",
    display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
    overflow: "hidden",
  },
  slotCta: {
    display: "flex", alignItems: "center", gap: 6,
    padding: "5px 8px",
    background: "rgba(220,50,140,0.08)",
    border: "1px solid rgba(220,50,140,0.28)",
    borderRadius: 6,
    fontFamily: "'Poppins', sans-serif",
  },
  slotCtaLabel: {
    fontSize: 9, letterSpacing: 1.4, textTransform: "uppercase",
    color: "#dc328c", fontWeight: 800, flexShrink: 0,
  },
  slotCtaText: {
    fontSize: 11, color: "rgba(255,255,255,0.9)", fontWeight: 600,
    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
  },
  slotMetaPreview: {
    padding: "10px 12px 12px",
    borderTop: "1px solid rgba(24,119,242,0.22)",
    background: "rgba(24,119,242,0.06)",
    display: "flex", flexDirection: "column", gap: 6,
    fontFamily: "'Poppins', sans-serif",
  },
  slotMetaHead: { display: "flex", alignItems: "center", gap: 5 },
  slotMetaIcon: {
    width: 14, height: 14, borderRadius: "50%",
    background: "#1877F2", color: "#fff",
    display: "flex", alignItems: "center", justifyContent: "center",
    fontSize: 9, fontWeight: 900,
    fontFamily: "Georgia, serif", fontStyle: "italic",
  },
  slotMetaLabel: {
    fontSize: 9, letterSpacing: 1.4, textTransform: "uppercase",
    fontWeight: 800, color: "#60A5FA", flex: 1,
  },
  slotMetaMore: {
    fontSize: 9, fontWeight: 800, color: "#93C5FD",
    padding: "1px 6px", borderRadius: 999,
    background: "rgba(24,119,242,0.2)",
  },
  slotMetaPrimary: {
    fontSize: 10.5, color: "rgba(255,255,255,0.82)", lineHeight: 1.5,
    display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical",
    overflow: "hidden",
  },
  slotMetaLine: {
    display: "flex", alignItems: "baseline", gap: 5,
    fontSize: 10.5, lineHeight: 1.4,
  },
  slotMetaLineLabel: {
    fontSize: 8.5, letterSpacing: 1.2, textTransform: "uppercase",
    color: "rgba(255,255,255,0.45)", fontWeight: 800, flexShrink: 0, minWidth: 46,
  },
  slotMetaLineText: {
    color: "rgba(255,255,255,0.9)", fontWeight: 600,
    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
  },
  reactionChip: {
    display: "inline-flex", alignItems: "center", gap: 4,
    padding: "3px 9px", borderRadius: 999,
    border: "1px solid",
    fontFamily: "'Poppins', sans-serif",
    fontSize: 10.5, fontWeight: 700, letterSpacing: 0.2,
  },
};

// ═════════════════════════════════════════════════════════════════════
// Creative Brief Card styles (crb)
// ═════════════════════════════════════════════════════════════════════
const crb = {
  wrap: {
    marginTop: 22, padding: 24,
    border: "1px solid rgba(180,118,255,0.28)",
    borderRadius: 16,
    background: "linear-gradient(135deg, rgba(180,118,255,0.06) 0%, rgba(220,50,140,0.03) 100%)",
    boxShadow: "0 6px 30px rgba(180,118,255,0.08)",
  },
  head: {
    display: "flex", alignItems: "flex-start", justifyContent: "space-between",
    gap: 20, cursor: "pointer", marginBottom: 10,
  },
  eyebrow: {
    fontFamily: "'Poppins', sans-serif", fontSize: 10.5, letterSpacing: 1.6,
    textTransform: "uppercase", color: "#B476FF", fontWeight: 700, marginBottom: 8,
  },
  h2: { fontSize: 22, fontWeight: 800, color: "#fff", letterSpacing: "-0.02em", margin: 0 },
  offer: { color: "rgba(255,255,255,0.7)", fontSize: 13.5, marginTop: 6, lineHeight: 1.5 },
  toggle: {
    padding: "6px 14px", borderRadius: 999,
    background: "rgba(180,118,255,0.1)", color: "#B476FF",
    border: "1px solid rgba(180,118,255,0.4)",
    fontSize: 11, fontWeight: 700, letterSpacing: 0.4,
    fontFamily: "'Poppins', sans-serif", cursor: "pointer",
    textTransform: "uppercase",
  },
  audience: {
    marginTop: 8, fontSize: 13, color: "rgba(255,255,255,0.75)", lineHeight: 1.5,
  },
  offerDetails: {
    marginTop: 6, fontSize: 13, color: "rgba(255,255,255,0.75)", lineHeight: 1.5,
  },
  smallLabel: {
    fontFamily: "'Poppins', sans-serif", fontSize: 9.5, letterSpacing: 1.4,
    color: "#4ADEDE", fontWeight: 700, marginRight: 8, textTransform: "uppercase",
  },
  section: { marginTop: 20 },
  blockLabel: {
    fontFamily: "'Poppins', sans-serif", fontSize: 10.5, letterSpacing: 1.6,
    textTransform: "uppercase", color: "rgba(255,255,255,0.6)", fontWeight: 700,
    marginBottom: 10,
  },
  hookList: { display: "flex", flexDirection: "column", gap: 10 },
  hookRow: {
    padding: "12px 14px", background: "rgba(255,255,255,0.04)",
    border: "1px solid rgba(255,255,255,0.08)", borderRadius: 10,
    display: "flex", gap: 12, alignItems: "flex-start",
  },
  hookTag: {
    padding: "3px 8px", background: "rgba(244,114,182,0.15)", color: "#F472B6",
    border: "1px solid rgba(244,114,182,0.4)", borderRadius: 999,
    fontSize: 9, fontFamily: "'Poppins', sans-serif", fontWeight: 700,
    letterSpacing: 0.8, textTransform: "uppercase", whiteSpace: "nowrap",
    flexShrink: 0,
  },
  hookText: { color: "rgba(255,255,255,0.9)", fontSize: 13.5, lineHeight: 1.55 },
  dirGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
    gap: 10,
  },
  dirCard: {
    padding: 14, background: "rgba(255,255,255,0.04)",
    border: "1px solid rgba(255,255,255,0.08)", borderRadius: 10,
  },
  dirFormat: {
    fontSize: 9.5, letterSpacing: 1.2, textTransform: "uppercase",
    color: "#4ADEDE", fontWeight: 700, marginBottom: 6,
    fontFamily: "'Poppins', sans-serif",
  },
  dirHead: { fontSize: 14, fontWeight: 700, color: "#fff", lineHeight: 1.3, marginBottom: 4 },
  dirSub: { fontSize: 12, color: "rgba(255,255,255,0.7)", lineHeight: 1.45, marginBottom: 8 },
  dirProof: { fontSize: 11, color: "#4ADE80", marginTop: 6 },
  dirCta: { fontSize: 11, color: "#F472B6", marginTop: 4, fontWeight: 600 },
  avoidWrap: {
    marginTop: 18, padding: 14, borderRadius: 10,
    background: "rgba(220,38,38,0.08)", border: "1px solid rgba(220,38,38,0.25)",
  },
  list: { margin: 0, paddingLeft: 20 },
  avoidItem: { color: "#FCA5A5", fontSize: 12.5, lineHeight: 1.55 },
};

// ═════════════════════════════════════════════════════════════════════
// Video Attachments styles (vid)
// ═════════════════════════════════════════════════════════════════════
const vid = {
  wrap: {
    marginTop: 32, padding: 22,
    background: "rgba(255,255,255,0.02)",
    border: "1px dashed rgba(255,255,255,0.12)",
    borderRadius: 14,
  },
  head: { marginBottom: 16 },
  h3: { fontSize: 18, fontWeight: 800, color: "#fff", margin: "6px 0 4px" },
  hint: { color: "rgba(255,255,255,0.55)", fontSize: 12.5, margin: 0 },
  form: { display: "flex", flexDirection: "column", gap: 10, marginBottom: 18 },
  input: {
    padding: "11px 14px", background: "rgba(255,255,255,0.05)",
    border: "1px solid rgba(255,255,255,0.12)", borderRadius: 8,
    color: "#fff", fontSize: 13.5, outline: "none",
  },
  formRow: { display: "flex", gap: 10, flexWrap: "wrap" },
  attachBtn: {
    padding: "10px 20px", background: "linear-gradient(135deg, #dc328c 0%, #B476FF 100%)",
    color: "#fff", border: "none", borderRadius: 8,
    fontSize: 13, fontWeight: 700, letterSpacing: 0.3, cursor: "pointer",
  },
  uploadBtn: {
    padding: "10px 20px", background: "rgba(255,255,255,0.06)",
    color: "#fff", border: "1px solid rgba(255,255,255,0.2)", borderRadius: 8,
    fontSize: 13, fontWeight: 700, letterSpacing: 0.3, cursor: "pointer",
    display: "inline-flex", alignItems: "center", justifyContent: "center",
  },
  empty: {
    padding: "18px 0", textAlign: "center", color: "rgba(255,255,255,0.4)",
    fontSize: 12.5, fontFamily: "'Poppins', sans-serif",
  },
  grid: { display: "flex", flexDirection: "column", gap: 12 },
  card: {
    background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: 10, overflow: "hidden",
  },
  player: {
    aspectRatio: "16 / 9",
    background: "#000", position: "relative",
  },
  iframe: { width: "100%", height: "100%", border: "none" },
  videoEl: { width: "100%", height: "100%", objectFit: "cover" },
  cardMeta: {
    padding: "10px 14px", display: "flex", justifyContent: "space-between",
    alignItems: "center", gap: 12,
  },
  sourceTag: {
    fontSize: 9.5, letterSpacing: 1.2, textTransform: "uppercase",
    color: "#4ADEDE", fontWeight: 700, fontFamily: "'Poppins', sans-serif",
  },
  cardHead: { color: "#fff", fontSize: 13, fontWeight: 600, marginTop: 2 },
  remove: {
    padding: "6px 12px", background: "rgba(220,38,38,0.15)", color: "#FCA5A5",
    border: "1px solid rgba(220,38,38,0.35)", borderRadius: 6,
    fontSize: 11, fontWeight: 700, cursor: "pointer",
    fontFamily: "'Poppins', sans-serif", letterSpacing: 0.3,
  },
};



if (typeof document !== "undefined" && !document.getElementById("adshub-detail-anim")) {
  const s = document.createElement("style");
  s.id = "adshub-detail-anim";
  s.textContent = `
    @keyframes adshub-pulse {
      0%, 100% { transform: scale(1); opacity: 0.9; box-shadow: 0 0 0 0 rgba(220,50,140,0.35); }
      50% { transform: scale(1.1); opacity: 1; box-shadow: 0 0 0 14px rgba(220,50,140,0); }
    }
    @keyframes skye-bounce {
      0%, 80%, 100% { transform: scale(0.5); opacity: 0.5; }
      40% { transform: scale(1); opacity: 1; }
    }
  `;
  document.head.appendChild(s);
}
